import type { Rect, ScreenshotSelection } from '@dreamteam/chat-protocol';
import { collectSelection, elementAtPoint, WIDGET_ATTR } from './capture/dom.js';

export interface ScreenshotResult {
  blob: Blob;
  width: number;
  height: number;
  selection: ScreenshotSelection;
  previewUrl: string;
}

export interface ScreenshotOptions {
  /** dom — рендер DOM в картинку (без запроса разрешений); display — захват вкладки через getDisplayMedia (точные пиксели, спросит разрешение). */
  method?: 'dom' | 'display';
  hint?: string;
  /** Вызывается, чтобы спрятать/показать сам виджет на время съёмки. */
  hideWidget?: (hidden: boolean) => void;
  accent?: string;
}

const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

/**
 * Режим «скриншот»: затемняет страницу, пользователь кликом и перетаскиванием
 * выделяет прямоугольник (или просто кликает по элементу). Возвращает PNG области
 * и DOM-элементы, попавшие в клик и внутрь прямоугольника.
 */
export function selectRegion(opts: ScreenshotOptions = {}): Promise<{ rect: Rect; point: { x: number; y: number } } | null> {
  return new Promise((resolve) => {
    const doc = document;
    const host = doc.createElement('div');
    host.setAttribute(WIDGET_ATTR, 'overlay');
    const accent = opts.accent ?? '#4f46e5';
    host.style.cssText = 'position:fixed;inset:0;z-index:2147483647;cursor:crosshair;touch-action:none;';
    const box = doc.createElement('div');
    box.style.cssText = `position:fixed;display:none;border:2px solid ${accent};border-radius:2px;box-shadow:0 0 0 100vmax rgba(15,23,42,.35);pointer-events:none;`;
    const shade = doc.createElement('div');
    shade.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,.35);pointer-events:none;';
    const hint = doc.createElement('div');
    hint.textContent = opts.hint ?? 'Выделите область или кликните по элементу · Esc — отмена';
    hint.style.cssText =
      'position:fixed;top:12px;left:50%;transform:translateX(-50%);padding:8px 14px;border-radius:8px;background:#0f172a;color:#fff;font:500 13px/1.3 system-ui,sans-serif;pointer-events:none;box-shadow:0 4px 16px rgba(0,0,0,.25);';
    const hover = doc.createElement('div');
    hover.style.cssText = `position:fixed;display:none;outline:2px dashed ${accent};outline-offset:1px;pointer-events:none;`;
    host.append(shade, hover, box, hint);
    doc.body.appendChild(host);

    let start: { x: number; y: number } | null = null;
    let current: Rect | null = null;

    const cleanup = () => {
      host.remove();
      doc.removeEventListener('keydown', onKey, true);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cleanup();
        resolve(null);
      }
    };
    doc.addEventListener('keydown', onKey, true);

    host.addEventListener('pointermove', (e) => {
      if (!start) {
        const el = elementAtPoint(doc, e.clientX, e.clientY);
        if (el) {
          const r = el.getBoundingClientRect();
          Object.assign(hover.style, { display: 'block', left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
        }
        return;
      }
      const x = Math.min(start.x, e.clientX);
      const y = Math.min(start.y, e.clientY);
      current = { x, y, width: Math.abs(e.clientX - start.x), height: Math.abs(e.clientY - start.y) };
      shade.style.display = 'none';
      hover.style.display = 'none';
      Object.assign(box.style, { display: 'block', left: `${x}px`, top: `${y}px`, width: `${current.width}px`, height: `${current.height}px` });
    });
    host.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      host.setPointerCapture?.(e.pointerId);
      start = { x: e.clientX, y: e.clientY };
    });
    host.addEventListener('pointerup', (e) => {
      if (!start) return;
      const point = start;
      let rect = current;
      if (!rect || rect.width < 6 || rect.height < 6) {
        // Клик без перетаскивания — берём элемент под курсором целиком.
        const el = elementAtPoint(doc, e.clientX, e.clientY);
        if (!el) {
          start = null;
          return;
        }
        const r = el.getBoundingClientRect();
        const pad = 4;
        rect = {
          x: Math.max(0, r.left - pad),
          y: Math.max(0, r.top - pad),
          width: Math.min(innerWidth, r.width + pad * 2),
          height: Math.min(innerHeight, r.height + pad * 2),
        };
      }
      cleanup();
      resolve({ rect: roundRect(rect), point });
    });
  });
}

function roundRect(r: Rect): Rect {
  return { x: Math.round(r.x), y: Math.round(r.y), width: Math.round(r.width), height: Math.round(r.height) };
}

export async function takeScreenshot(opts: ScreenshotOptions = {}): Promise<ScreenshotResult | null> {
  opts.hideWidget?.(true);
  try {
    const region = await selectRegion(opts);
    if (!region) return null;
    const { rect, point } = region;
    const partial = collectSelection(document, rect, point);
    const selection: ScreenshotSelection = {
      ...partial,
      scroll: { x: Math.round(scrollX), y: Math.round(scrollY) },
      viewport: { width: innerWidth, height: innerHeight, dpr: devicePixelRatio || 1 },
      url: location.href,
    };
    await nextFrame();
    const canvas = opts.method === 'display' ? await captureDisplay(rect) : await captureDom(rect);
    const blob = await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('toBlob failed'))), 'image/png'));
    return { blob, width: canvas.width, height: canvas.height, selection, previewUrl: URL.createObjectURL(blob) };
  } finally {
    opts.hideWidget?.(false);
  }
}

async function captureDom(rect: Rect): Promise<HTMLCanvasElement> {
  const { domToCanvas } = await import('modern-screenshot');
  const root = document.documentElement;
  const fullH = Math.max(root.scrollHeight, document.body?.scrollHeight ?? 0);
  const scale = Math.min(devicePixelRatio || 1, fullH > 12000 ? 1 : 2);
  const full = await domToCanvas(root, {
    scale,
    width: root.scrollWidth,
    height: fullH,
    backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
    filter: (node: Node) => !(node instanceof Element && node.hasAttribute(WIDGET_ATTR)),
  });
  return crop(full, (rect.x + scrollX) * scale, (rect.y + scrollY) * scale, rect.width * scale, rect.height * scale);
}

async function captureDisplay(rect: Rect): Promise<HTMLCanvasElement> {
  const stream = await (navigator.mediaDevices as any).getDisplayMedia({ video: { displaySurface: 'browser' }, audio: false, preferCurrentTab: true });
  try {
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await nextFrame();
    const sx = video.videoWidth / innerWidth;
    const sy = video.videoHeight / innerHeight;
    const c = document.createElement('canvas');
    c.width = Math.round(rect.width * sx);
    c.height = Math.round(rect.height * sy);
    c.getContext('2d')!.drawImage(video, rect.x * sx, rect.y * sy, rect.width * sx, rect.height * sy, 0, 0, c.width, c.height);
    return c;
  } finally {
    stream.getTracks().forEach((t: MediaStreamTrack) => t.stop());
  }
}

function crop(src: HTMLCanvasElement, x: number, y: number, w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  c.getContext('2d')!.drawImage(src, Math.round(x), Math.round(y), c.width, c.height, 0, 0, c.width, c.height);
  return c;
}
